import { useEffect, useState } from 'react';

export interface ThemeColors {
  background: {
    primary: string;
    secondary: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  primary: string;
  success: string;
  warning: string;
  info: string;
  error: string;
}

const defaultTheme: ThemeColors = {
  background: {
    primary: '#0A0A0A',
    secondary: '#161717',
  },
  text: {
    primary: '#E9E9F0',
    secondary: '#CECECE',
  },
  primary: '#DBB958',
  success: '#00E23F',
  warning: '#FFA500',
  info: '#0000FF',
  error: '#FF0000',
};

// Chave para o localStorage
const THEME_STORAGE_KEY = 'agnostic-platform-theme';

// Função para salvar tema no localStorage
const saveThemeToStorage = (theme: ThemeColors) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    console.log('Tema salvo no localStorage:', theme);
  } catch (error) {
    console.error('Erro ao salvar tema no localStorage:', error);
  }
};

// Função para carregar tema do localStorage
const loadThemeFromStorage = (): ThemeColors | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      const theme = JSON.parse(stored);
      console.log('Tema carregado do localStorage:', theme);
      return theme;
    }
  } catch (error) {
    console.error('Erro ao carregar tema do localStorage:', error);
  }
  return null;
};

export const useTheme = () => {
  const [theme, setTheme] = useState<ThemeColors>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        // Primeiro tenta carregar do localStorage
        const storedTheme = loadThemeFromStorage();

        const publicTheme = '/assets/theme/colors.json';
        const response = await fetch(publicTheme);

        if (!response.ok) {
          throw new Error('Falha ao carregar o tema');
        }

        const data = await response.json();

        // Validação básica da estrutura do tema (sem exigir success, warning, info, error)
        if (
          !data ||
          !data.background ||
          !data.background.primary ||
          !data.background.secondary ||
          !data.text ||
          !data.text.primary ||
          !data.text.secondary ||
          !data.primary
        ) {
          throw new Error('Formato do tema inválido');
        }

        // Sempre define as cores fixas
        const newTheme = {
          ...data,
          success: '#00E23F',
          warning: '#FFA500',
          info: '#0000FF',
          error: '#FF0000',
        };

        setTheme(newTheme);

        // Salva no localStorage
        saveThemeToStorage(newTheme);

        console.log('Tema carregado com sucesso da pasta public e salvo no localStorage');
      } catch (error) {
        console.error('Erro ao carregar tema:', error);
        setError(error as Error);

        // Se falhar, usa o tema do localStorage ou default
        const storedTheme = loadThemeFromStorage();
        if (storedTheme) {
          setTheme(storedTheme);
          console.log('Usando tema do localStorage como fallback');
        } else {
          setTheme(defaultTheme);
          saveThemeToStorage(defaultTheme);
          console.log('Usando tema default como fallback');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTheme();
  }, []);

  return {
    theme,
    isLoading,
    error,
  };
};

// Hook para usar em iframes - carrega tema do localStorage
export const useIframeTheme = () => {
  const [theme, setTheme] = useState<ThemeColors>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStoredTheme = () => {
      const storedTheme = loadThemeFromStorage();
      if (storedTheme) {
        setTheme(storedTheme);
      } else {
        setTheme(defaultTheme);
      }
      setIsLoading(false);
    };

    loadStoredTheme();

    // Listener para mudanças no localStorage (caso a app principal atualize o tema)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        try {
          const newTheme = JSON.parse(e.newValue);
          setTheme(newTheme);
          console.log('Tema atualizado via localStorage event:', newTheme);
        } catch (error) {
          console.error('Erro ao processar mudança de tema:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return {
    theme,
    isLoading,
  };
};
